import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';

interface Expense {
  id: number;
  date: string;
  amount: number;
  type: 'EXPENSE' | 'RECEIPT';
  notes: string;
  file_path: string;
  created_at: string;
}

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  expense?: Expense | null;
}

export default function ExpenseForm({ open, onClose, onSubmit, expense }: ExpenseFormProps) {
  const [date, setDate] = useState<Date | null>(new Date());
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'EXPENSE' | 'RECEIPT'>('EXPENSE');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (expense) {
      setDate(new Date(expense.date));
      setAmount(expense.amount.toString());
      setType(expense.type);
      setNotes(expense.notes);
      setFile(null);
    } else {
      setDate(new Date());
      setAmount('');
      setType('EXPENSE');
      setNotes('');
      setFile(null);
    }
  }, [expense]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!date || !amount || !notes) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('date', date.toISOString().split('T')[0]);
      formData.append('amount', amount);
      formData.append('type', type);
      formData.append('notes', notes);
      if (file) {
        formData.append('file', file);
      }

      if (expense) {
        await api.put(`/expenses/${expense.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.post('/expenses', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      onSubmit();
      handleClose();
    } catch (error) {
      console.error('Error saving expense:', error);
      setError('Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDate(new Date());
    setAmount('');
    setType('EXPENSE');
    setNotes('');
    setFile(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{expense ? 'Edit' : 'Add New'} {type === 'EXPENSE' ? 'Expense' : 'Receipt'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                label="Type"
                onChange={(e) => setType(e.target.value as 'EXPENSE' | 'RECEIPT')}
              >
                <MenuItem value="EXPENSE">Expense</MenuItem>
                <MenuItem value="RECEIPT">Receipt</MenuItem>
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={date}
                onChange={(newValue) => setDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              required
            />

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 1 }}
            >
              {expense ? 'Change File (Optional)' : 'Upload File (Optional)'}
              <input
                type="file"
                hidden
                name="document"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            {file && (
              <Box sx={{ mt: 1 }}>
                Selected file: {file.name}
              </Box>
            )}
            {expense && !file && expense.file_path && (
              <Box sx={{ mt: 1 }}>
                Current file: {
                  expense.file_path.startsWith('http') 
                    ? expense.file_path.split('/').pop() 
                    : expense.file_path.split('/').pop()?.replace('compressed_', '')
                }
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : expense ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
} 